'use client';

import type { WorkspaceRuntimeDialogCallbacks } from '@/cms/components/blocks/workspace/workspace-1/contracts/dialog-callbacks';
import type { WorkspaceRuntimeActionCallbacks } from '@/cms/components/blocks/workspace/workspace-1/contracts/dialog-callbacks';
import type { WorkspaceStudioRenderModel } from '@/cms/components/blocks/workspace/workspace-1/contracts/server-model';
import type { WorkspaceStudioBlockClient } from '@/cms/components/blocks/workspace/workspace-1/shell/client';
import { WorkspaceStudioSkeleton } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-studio-skeleton';
import {
  type WorkspaceLoadErrorCopy,
  useWorkspaceLoadErrorToast,
} from '@/cms/components/blocks/workspace/workspace-load-error';
import {
  type CmsWorkspaceLaunchDetail,
  replayCmsWorkspaceLaunch,
} from '@/cms/components/blocks/workspace/workspace-session-controller';
import {
  loadWorkspaceToolModel,
  resetWorkspaceToolResourceCacheForTests,
} from '@/cms/components/blocks/workspace/workspace-tool-resource';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type WorkspaceClientComponent = typeof WorkspaceStudioBlockClient;
type WorkspaceClientModule = {
  WorkspaceStudioBlockClient: WorkspaceClientComponent;
};

let workspaceClientModulePromise: Promise<WorkspaceClientModule> | null = null;
const workspaceModelPromises = new Map<
  string,
  Promise<WorkspaceStudioRenderModel>
>();

function getPageBoundWorkspaceModelCacheKey(params: {
  toolId: string;
  locale: string;
}) {
  return `${params.locale}:${params.toolId}`;
}

function loadWorkspaceClientModule() {
  if (workspaceClientModulePromise) {
    return workspaceClientModulePromise;
  }

  workspaceClientModulePromise = import(
    /* webpackFetchPriority: "high" */
    '@/cms/components/blocks/workspace/workspace-1/shell/client'
  ).catch((error) => {
    workspaceClientModulePromise = null;
    throw error;
  });

  return workspaceClientModulePromise;
}

function loadPageBoundWorkspaceModel(params: {
  toolId: string;
  locale: string;
}) {
  const cacheKey = getPageBoundWorkspaceModelCacheKey(params);
  const existingPromise = workspaceModelPromises.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  const promise = loadWorkspaceToolModel({
    ...params,
    surface: 'launched-runtime',
  }).catch((error) => {
    if (workspaceModelPromises.get(cacheKey) === promise) {
      workspaceModelPromises.delete(cacheKey);
    }
    throw error;
  });
  workspaceModelPromises.set(cacheKey, promise);
  return promise;
}

export async function preloadPageBoundWorkspaceRuntime(params: {
  toolId: string;
  locale: string;
}) {
  await Promise.all([
    loadPageBoundWorkspaceModel(params),
    loadWorkspaceClientModule(),
  ]);
}

export function resetPageBoundWorkspaceRuntimeForTests() {
  workspaceClientModulePromise = null;
  workspaceModelPromises.clear();
  resetWorkspaceToolResourceCacheForTests();
}

export function SiteRuntimePageBoundWorkspaceHost({
  dialogCallbacks,
  actionCallbacks,
  errorCopy,
  launchDetail,
  pageHero,
  locale,
  onShellReady,
  toolId,
}: {
  errorCopy: WorkspaceLoadErrorCopy;
  dialogCallbacks?: WorkspaceRuntimeDialogCallbacks;
  actionCallbacks?: WorkspaceRuntimeActionCallbacks;
  launchDetail: CmsWorkspaceLaunchDetail | null;
  locale: string;
  onShellReady?: () => void;
  pageHero?: WorkspaceStudioRenderModel['shell']['hero'];
  toolId: string;
}) {
  const [ClientComponent, setClientComponent] =
    useState<WorkspaceClientComponent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [model, setModel] = useState<WorkspaceStudioRenderModel | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const replayedLaunchDetailRef = useRef<CmsWorkspaceLaunchDetail | null>(null);
  const retryWorkspaceLoad = useCallback(() => {
    setLoadError(null);
    setRetryNonce((current) => current + 1);
  }, []);

  useLayoutEffect(() => {
    onShellReady?.();
  }, [onShellReady]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setModel(null);

    if (!toolId) return;
    loadPageBoundWorkspaceModel({ toolId, locale })
      .then((nextModel) => {
        if (!cancelled) {
          setModel(nextModel);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('model');
          setModel(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [locale, toolId, retryNonce]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);

    loadWorkspaceClientModule()
      .then((module) => {
        if (!cancelled) {
          setClientComponent(() => module.WorkspaceStudioBlockClient);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('client');
          setClientComponent(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [retryNonce]);

  useEffect(() => {
    if (!launchDetail || !model || !ClientComponent) {
      return;
    }

    if (replayedLaunchDetailRef.current === launchDetail) {
      return;
    }

    replayedLaunchDetailRef.current = launchDetail;
    const frame = window.requestAnimationFrame(() => {
      replayCmsWorkspaceLaunch(launchDetail);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [ClientComponent, launchDetail, model]);

  useWorkspaceLoadErrorToast({
    errorCopy,
    hasError: Boolean(loadError),
    onRetry: retryWorkspaceLoad,
  });

  if (loadError) {
    return <WorkspaceStudioSkeleton hero={pageHero} />;
  }

  if (!launchDetail || !model || !ClientComponent) {
    return <WorkspaceStudioSkeleton hero={pageHero} />;
  }

  return (
    <ClientComponent
      forceAuthSessionRead={Boolean(launchDetail.forceAuthSessionRead)}
      model={model}
      runtimeDialogCallbacks={dialogCallbacks}
      runtimeActionCallbacks={actionCallbacks}
    />
  );
}
